import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaGithub, FaLinkedin, FaTwitter, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE } from '../config';
import { useLanguage } from '../context/LanguageContext';
import Newsletter from '../components/newsletter';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Contact = () => {
  const [about, setAbout] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coords, setCoords] = useState(null);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/portfolio`);
        const data = await res.json();
        setAbout(data.about || {});
      } catch (error) {
        console.error('Error fetching about:', error);
        setAbout({
          email: process.env.REACT_APP_EMAIL || 'bandabernard917@gmail.com',
          location: 'George Lusaka, Zambia',
          github: 'https://github.com/BenBand',
          linkedin: 'https://www.linkedin.com/in/bernard-banda-6995b633b/',
        });
      }
    };

    fetchAbout();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok) {
        setStatus({ type: 'success', message: t('contact.successMessage') });
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus({ type: 'error', message: data.error || t('contact.errorMessage') });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({ type: 'error', message: t('contact.errorMessage') });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!about?.location) return;

    const geocodeLocation = async () => {
      try {
        setMapLoading(true);
        setMapError(false);
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(about.location)}&limit=1`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        if (data && data[0]) {
          setCoords({
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon),
          });
        } else {
          setMapError(true);
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setMapError(true);
      } finally {
        setMapLoading(false);
      }
    };

    geocodeLocation();
  }, [about?.location]);

  useEffect(() => {
    if (status.type === 'success' && status.message) {
      const timer = setTimeout(() => {
        setStatus({ type: '', message: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <div className="contact-page">
      {status.message && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            padding: '2.5rem',
            borderRadius: '20px',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: '450px',
            width: '100%',
            textAlign: 'center',
            position: 'relative'
          }}>
            <button
              onClick={() => setStatus({ type: '', message: '' })}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: 'var(--text-light)'
              }}
            >
              ×
            </button>
            <div style={{ fontSize: '3rem', marginBottom: '1rem', color: status.type === 'success' ? '#10b981' : '#ef4444' }}>
              {status.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
            </div>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-dark)', lineHeight: '1.6' }}>
              {status.message}
            </p>
          </div>
        </div>
      )}

      <section className="section" style={{ paddingTop: '8rem', background: 'var(--bg-light)' }}>
        <h2 className="section-title">{t('contact.title')}</h2>
        <p style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 3rem', color: 'var(--text-light)', fontSize: '1.1rem' }}>
          {t('contact.subtitle')}
        </p>

        <div className="contact-container" data-tour="contact-form">
          <div className="contact-info-section">
            <h3>{t('contact.contactInfo')}</h3>
            <div className="contact-item">
              <div className="contact-icon">
                <FaEnvelope />
              </div>
              <div className="contact-text">
                <h4>{t('contact.email')}</h4>
                <p><a href={`mailto:${about?.email || 'bandabernard917@gmail.com'}`}>{about?.email || 'bandabernard917@gmail.com'}</a></p>
              </div>
            </div>

            <div className="contact-item">
              <div className="contact-icon">
                <FaMapMarkerAlt />
              </div>
              <div className="contact-text">
                <h4>{t('contact.location')}</h4>
                <p>{about?.location || 'George Lusaka, Zambia'}</p>
              </div>
            </div>

            <div className="social-section" style={{ marginTop: '2rem' }}>
              <h4 style={{ marginBottom: '1rem', color: 'var(--text-dark)' }}>{t('footer.connect')}</h4>
              <div className="social-links" style={{ justifyContent: 'flex-start' }}>
                <a href="https://github.com/BenBand" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaGithub />
                </a>
                <a href="https://www.linkedin.com/in/bernard-banda-6995b633b/" target="_blank" rel="noopener noreferrer" className="social-link">
                  <FaLinkedin />
                </a>
              </div>
            </div>
          </div>

          <div className="contact-form-section">
            <h3>{t('contact.sendMessage')}</h3>
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">{t('contact.name')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.name')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t('contact.email')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.email')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">{t('contact.subject')} *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.subject')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">{t('contact.message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder={t('contact.message')}
                ></textarea>
              </div>

              <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ width: '100%', justifyContent: 'center' }}>
                {isSubmitting ? (
                  <>{t('contact.sending')}</>
                ) : (
                  <><FaPaperPlane /> {t('contact.send')}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: '#fff' }}>
        <h2 className="section-title">{t('contact.location')}</h2>
        <div style={{ maxWidth: '1000px', margin: '0 auto', height: '400px', borderRadius: '20px', overflow: 'hidden', boxShadow: 'var(--shadow-lg)' }}>
          {mapLoading && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
              <p>Loading map...</p>
            </div>
          )}
          {mapError && !mapLoading && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
              <p>Unable to load map</p>
            </div>
          )}
          {coords && !mapLoading && !mapError && (
            <MapContainer center={[coords.lat, coords.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[coords.lat, coords.lng]}>
                <Popup>
                  {about?.location || 'George Lusaka, Zambia'}
                </Popup>
              </Marker>
            </MapContainer>
          )}
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default Contact;
